import { useEffect, useMemo, useState } from 'react';
import Sheet from './Sheet';
import { useT } from '../context/LocaleContext';
import {
  DEFAULT_ADVISOR_TABS,
  DEFAULT_MECHANIC_TABS,
  DESK_TAB_IDS,
  DESK_TABS,
  defaultTabsForRole,
  normalizeDeskTabs,
} from '../lib/deskNav';
import { displayName } from '../lib/format';
import { supabase } from '../lib/supabase';

const blankStaff = {
  first_name: '',
  last_name: '',
  email: '',
  phone: '',
  password: '',
  role: '',
  branch_id: '',
};

function DeskTabPicker({ value, onChange, t }) {
  function toggle(id) {
    onChange(
      value.includes(id) ? value.filter((item) => item !== id) : [...value, id]
    );
  }

  return (
    <>
      <ul className="access-list">
        {DESK_TABS.map((tab) => {
          const on = value.includes(tab.id);
          return (
            <li key={tab.id}>
              <label className={on ? 'access-row on' : 'access-row'}>
                <input
                  type="checkbox"
                  checked={on}
                  onChange={() => toggle(tab.id)}
                />
                <span className="material-symbol" aria-hidden="true">
                  {tab.icon}
                </span>
                <strong>{t(tab.labelKey)}</strong>
              </label>
            </li>
          );
        })}
      </ul>
      <div className="row-actions">
        <button
          type="button"
          className="ghost"
          onClick={() => onChange([...DESK_TAB_IDS])}
        >
          {t('team.selectAll')}
        </button>
        <button
          type="button"
          className="ghost"
          onClick={() => onChange([...DEFAULT_MECHANIC_TABS])}
        >
          {t('team.floorBookings')}
        </button>
        <button
          type="button"
          className="ghost"
          onClick={() => onChange([...DEFAULT_ADVISOR_TABS])}
        >
          {t('team.exceptSetup')}
        </button>
      </div>
    </>
  );
}

export default function StaffPanel({
  isManager,
  roles,
  roleOptions,
  defaultRole,
  addLabel,
  createTitle,
  emptyText,
  optionalAdd = false,
  requireBranch = false,
  tabAccess = false,
  tabAccessRoles = null,
  allowPasswordReset = false,
}) {
  const t = useT();
  const [staff, setStaff] = useState([]);
  const [branches, setBranches] = useState([]);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [savingId, setSavingId] = useState('');
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(blankStaff);
  const [creating, setCreating] = useState(false);
  const [accessFor, setAccessFor] = useState(null);
  const [accessDraft, setAccessDraft] = useState([]);
  const [savingAccess, setSavingAccess] = useState(false);
  const [passwordFor, setPasswordFor] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [resetting, setResetting] = useState(false);
  const [resetError, setResetError] = useState('');

  function hasTabAccess(role) {
    if (!tabAccess) return false;
    if (tabAccessRoles?.length) return tabAccessRoles.includes(role);
    return true;
  }

  function canResetPassword(role) {
    if (!allowPasswordReset || !isManager) return false;
    return role === 'technician' || role === 'advisor';
  }

  async function load() {
    const [{ data: staffRows, error: staffError }, { data: branchRows, error: branchError }] =
      await Promise.all([
        supabase
          .from('profiles')
          .select('id, first_name, last_name, full_name, email, phone, role, branch_id, desk_tabs')
          .in('role', roles)
          .order('full_name'),
        supabase.from('branches').select('id, name').order('name'),
      ]);
    if (staffError || branchError) {
      setError(staffError?.message || branchError?.message);
      return;
    }
    setStaff(staffRows || []);
    setBranches(branchRows || []);
  }

  const roleKey = roles.join(',');

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roleKey]);

  const branchName = useMemo(() => {
    const map = Object.fromEntries(branches.map((item) => [item.id, item.name]));
    return (id) => map[id] || t('team.unassigned');
  }, [branches, t]);

  function startCreate() {
    setDraft({
      ...blankStaff,
      role: defaultRole,
      branch_id: requireBranch ? branches[0]?.id || '' : '',
    });
    setAccessDraft(defaultTabsForRole(defaultRole));
    setError('');
    setNotice('');
    setOpen(true);
  }

  function openAccess(row) {
    const current = normalizeDeskTabs(row.desk_tabs);
    setAccessFor(row);
    setAccessDraft(current || defaultTabsForRole(row.role));
    setError('');
    setNotice('');
  }

  function openPasswordReset(row) {
    setPasswordFor(row);
    setNewPassword('');
    setResetError('');
    setError('');
    setNotice('');
  }

  async function saveAccess(event) {
    event.preventDefault();
    if (!isManager || !accessFor) return;
    const next = normalizeDeskTabs(accessDraft);
    if (!next || next.length === 0) {
      setError(t('team.pickOneTab'));
      return;
    }
    setSavingAccess(true);
    setError('');
    const { error: saveError } = await supabase
      .from('profiles')
      .update({ desk_tabs: next })
      .eq('id', accessFor.id);
    setSavingAccess(false);
    if (saveError) {
      setError(saveError.message);
      return;
    }
    setAccessFor(null);
    load();
  }

  async function resetPassword(event) {
    event.preventDefault();
    if (!isManager || !passwordFor) return;
    if (newPassword.trim().length < 8) {
      setResetError(t('team.passwordMin'));
      return;
    }
    setResetting(true);
    setResetError('');
    const { error: rpcError } = await supabase.rpc('update_staff', {
      p_staff_id: passwordFor.id,
      p_password: newPassword.trim(),
    });
    setResetting(false);
    if (rpcError) {
      setResetError(rpcError.message);
      return;
    }
    setPasswordFor(null);
    setNewPassword('');
    setNotice(t('team.resetOk'));
  }

  async function createStaff(event) {
    event.preventDefault();
    if (!isManager) return;
    const wantsTabs = hasTabAccess(draft.role);
    const tabs = wantsTabs ? normalizeDeskTabs(accessDraft) : null;
    if (wantsTabs && (!tabs || tabs.length === 0)) {
      setError(t('team.pickOneTab'));
      return;
    }
    setCreating(true);
    setError('');
    setNotice('');
    const { data: newId, error: createError } = await supabase.rpc('create_staff', {
      p_email: draft.email.trim(),
      p_password: draft.password,
      p_first_name: draft.first_name.trim(),
      p_last_name: draft.last_name.trim(),
      p_phone: draft.phone.trim(),
      p_role: draft.role,
      p_branch_id: draft.branch_id || null,
    });
    if (createError) {
      setCreating(false);
      setError(createError.message);
      return;
    }
    if (newId && tabs) {
      const { error: tabsError } = await supabase
        .from('profiles')
        .update({ desk_tabs: tabs })
        .eq('id', newId);
      if (tabsError) {
        setCreating(false);
        setError(tabsError.message);
        setOpen(false);
        load();
        return;
      }
    }
    setCreating(false);
    setOpen(false);
    load();
  }

  async function assign(profile, next) {
    if (!isManager) return;
    if (next.role && next.role !== 'manager' && !next.branch_id && !profile.branch_id) {
      setError(t('team.assignBranch'));
      return;
    }
    setSavingId(profile.id);
    setError('');
    setNotice('');
    const payload = {};
    if (next.role) payload.role = next.role;
    if (Object.prototype.hasOwnProperty.call(next, 'branch_id')) {
      payload.branch_id = next.branch_id || null;
    }
    if (next.role === 'manager') {
      payload.desk_tabs = null;
    } else if (next.role === 'advisor' && !normalizeDeskTabs(profile.desk_tabs)) {
      payload.desk_tabs = defaultTabsForRole('advisor');
    } else if (next.role === 'technician' && !normalizeDeskTabs(profile.desk_tabs)) {
      payload.desk_tabs = defaultTabsForRole('technician');
    }
    const { error: saveError } = await supabase
      .from('profiles')
      .update(payload)
      .eq('id', profile.id);
    setSavingId('');
    if (saveError) {
      setError(saveError.message);
      return;
    }
    load();
  }

  const addButton = isManager ? (
    <button
      type="button"
      className={optionalAdd ? 'ghost' : 'primary'}
      onClick={startCreate}
    >
      {addLabel}
    </button>
  ) : null;

  const showCreateTabs = hasTabAccess(draft.role);

  return (
    <>
      <div className="panel-head">
        <span />
        {addButton}
      </div>
      {error && <p className="form-error">{error}</p>}
      {notice && <p className="form-ok">{notice}</p>}
      {staff.length === 0 ? (
        <p className="empty">{emptyText}</p>
      ) : (
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>{t('team.name')}</th>
                <th>{t('team.email')}</th>
                <th>{t('team.phone')}</th>
                {roleOptions.length > 1 && <th>{t('team.role')}</th>}
                <th>{t('team.branch')}</th>
                {tabAccess && <th>{t('team.tabs')}</th>}
                {isManager && <th></th>}
              </tr>
            </thead>
            <tbody>
              {staff.map((row) => {
                const tabs = normalizeDeskTabs(row.desk_tabs);
                return (
                  <tr key={row.id}>
                    <td>{displayName(row)}</td>
                    <td>{row.email || '—'}</td>
                    <td>{row.phone || '—'}</td>
                    {roleOptions.length > 1 && (
                      <td>
                        {isManager ? (
                          <select
                            value={row.role}
                            disabled={savingId === row.id}
                            onChange={(e) => assign(row, { role: e.target.value })}
                          >
                            {roleOptions.map((role) => (
                              <option key={role.value} value={role.value}>
                                {role.label}
                              </option>
                            ))}
                          </select>
                        ) : (
                          roleOptions.find((role) => role.value === row.role)?.label ||
                          row.role
                        )}
                      </td>
                    )}
                    <td>
                      {isManager ? (
                        <select
                          value={row.branch_id || ''}
                          disabled={savingId === row.id}
                          onChange={(e) =>
                            assign(row, { branch_id: e.target.value || null })
                          }
                        >
                          <option value="">{t('team.unassigned')}</option>
                          {branches.map((branch) => (
                            <option key={branch.id} value={branch.id}>
                              {branch.name}
                            </option>
                          ))}
                        </select>
                      ) : (
                        branchName(row.branch_id)
                      )}
                    </td>
                    {tabAccess && (
                      <td>
                        <span className="tab-count">
                          {hasTabAccess(row.role)
                            ? tabs
                              ? t('team.tabsCount', {
                                  n: tabs.length,
                                  total: DESK_TABS.length,
                                })
                              : t('team.tabsAll')
                            : t('team.tabsAll')}
                        </span>
                      </td>
                    )}
                    {isManager && (
                      <td>
                        <div className="row-actions">
                          {hasTabAccess(row.role) && (
                            <button
                              type="button"
                              className="icon-btn"
                              title={t('team.chooseTabs')}
                              aria-label={t('team.chooseTabsFor', {
                                name: displayName(row),
                              })}
                              disabled={savingId === row.id}
                              onClick={() => openAccess(row)}
                            >
                              <span className="material-symbol">settings</span>
                            </button>
                          )}
                          {canResetPassword(row.role) && (
                            <button
                              type="button"
                              className="ghost"
                              disabled={savingId === row.id}
                              onClick={() => openPasswordReset(row)}
                            >
                              {t('team.resetPassword')}
                            </button>
                          )}
                          <button
                            type="button"
                            className="ghost danger"
                            disabled={savingId === row.id}
                            onClick={() =>
                              assign(row, { role: 'customer', branch_id: null })
                            }
                          >
                            {t('common.remove')}
                          </button>
                        </div>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {open && (
        <Sheet
          variant="modal"
          title={createTitle}
          subtitle={t('setup.team')}
          onClose={() => setOpen(false)}
        >
          <form className="sheet-form" onSubmit={createStaff}>
            <p className="lede" style={{ marginTop: 0 }}>
              {t('team.createLede')}
            </p>
            <div className="form-row">
              <label>
                {t('team.firstName')}
                <input
                  required
                  value={draft.first_name}
                  onChange={(e) => setDraft({ ...draft, first_name: e.target.value })}
                />
              </label>
              <label>
                {t('team.lastName')}
                <input
                  value={draft.last_name}
                  onChange={(e) => setDraft({ ...draft, last_name: e.target.value })}
                />
              </label>
            </div>
            <label>
              {t('team.email')}
              <input
                required
                type="email"
                value={draft.email}
                onChange={(e) => setDraft({ ...draft, email: e.target.value })}
              />
            </label>
            <label>
              {t('team.phone')}
              <input
                value={draft.phone}
                onChange={(e) => setDraft({ ...draft, phone: e.target.value })}
              />
            </label>
            <label>
              {t('team.tempPassword')}
              <input
                required
                type="text"
                minLength={8}
                autoComplete="new-password"
                value={draft.password}
                onChange={(e) => setDraft({ ...draft, password: e.target.value })}
              />
            </label>
            <div className="form-row">
              {roleOptions.length > 1 && (
                <label>
                  {t('team.role')}
                  <select
                    value={draft.role}
                    onChange={(e) => {
                      const role = e.target.value;
                      setDraft({ ...draft, role });
                      if (hasTabAccess(role)) {
                        setAccessDraft(defaultTabsForRole(role));
                      }
                    }}
                  >
                    {roleOptions.map((role) => (
                      <option key={role.value} value={role.value}>
                        {role.label}
                      </option>
                    ))}
                  </select>
                </label>
              )}
              <label>
                {t('team.branch')}
                <select
                  required={requireBranch || draft.role !== 'manager'}
                  value={draft.branch_id}
                  onChange={(e) => setDraft({ ...draft, branch_id: e.target.value })}
                >
                  <option value="">{t('team.unassigned')}</option>
                  {branches.map((branch) => (
                    <option key={branch.id} value={branch.id}>
                      {branch.name}
                    </option>
                  ))}
                </select>
              </label>
            </div>
            {showCreateTabs && (
              <div className="access-block">
                <strong>{t('team.deskTabs')}</strong>
                <p className="lede" style={{ marginTop: 6 }}>
                  {t('team.createTabsLede')}
                </p>
                <DeskTabPicker
                  value={accessDraft}
                  onChange={setAccessDraft}
                  t={t}
                />
              </div>
            )}
            <div className="row-actions">
              <button type="submit" className="primary" disabled={creating}>
                {creating ? t('team.creating') : t('team.createLogin')}
              </button>
              <button type="button" className="ghost" onClick={() => setOpen(false)}>
                {t('common.cancel')}
              </button>
            </div>
          </form>
        </Sheet>
      )}
      {accessFor && (
        <Sheet
          variant="modal"
          title={t('team.tabsTitle', { name: displayName(accessFor) })}
          subtitle={t('team.access')}
          onClose={() => setAccessFor(null)}
        >
          <form className="sheet-form" onSubmit={saveAccess}>
            <p className="lede" style={{ marginTop: 0 }}>
              {accessFor.role === 'advisor'
                ? t('team.advisorTabsLede')
                : t('team.mechanicTabsLede')}
            </p>
            <DeskTabPicker value={accessDraft} onChange={setAccessDraft} t={t} />
            <div className="row-actions">
              <button type="submit" className="primary" disabled={savingAccess}>
                {savingAccess ? t('common.saving') : t('team.saveAccess')}
              </button>
              <button
                type="button"
                className="ghost"
                onClick={() => setAccessFor(null)}
              >
                {t('common.cancel')}
              </button>
            </div>
          </form>
        </Sheet>
      )}
      {passwordFor && (
        <Sheet
          variant="modal"
          title={t('team.resetPasswordFor', { name: displayName(passwordFor) })}
          subtitle={t('setup.team')}
          onClose={() => setPasswordFor(null)}
        >
          <form className="sheet-form" onSubmit={resetPassword}>
            <p className="lede" style={{ marginTop: 0 }}>
              {t('team.passwordHint')}
            </p>
            {resetError && <p className="form-error">{resetError}</p>}
            <label>
              {t('team.newPassword')}
              <input
                required
                type="text"
                minLength={8}
                autoComplete="new-password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
              />
            </label>
            <div className="row-actions">
              <button type="submit" className="primary" disabled={resetting}>
                {resetting ? t('team.resetting') : t('team.resetPassword')}
              </button>
              <button
                type="button"
                className="ghost"
                onClick={() => setPasswordFor(null)}
              >
                {t('common.cancel')}
              </button>
            </div>
          </form>
        </Sheet>
      )}
    </>
  );
}
